// =============================================================================
// ZAKŁADKA „POWIĄZANIA KART” — karty z Receptora czekające na decyzję (UD-22b)
//
// Stan źródła kartoteki, otwarte propozycje w sekcjach według klasy, odrzucone,
// włączenie i wyłączenie przyjmowania kart, bezpiecznik nagrobka. Decyzje (stan
// ekranu, słowa, walidacja) liczą czyste funkcje z `powiazaniaKart.ts`; tu jest
// rysowanie i zapytania. Wszystko idzie przez receptor-core — tabele kartoteki nie
// mają polityki odczytu dla zalogowanego człowieka.
//
// CZTERY RZECZY, KTÓRE TEN EKRAN PILNUJE:
//
//  * BEZ PRAWA I BEZ USŁUGI NIE PYTA. Kierownik i partner dostaliby 403, brak adresu
//    usługi — błąd sieci; ekran mówi wtedy dlaczego, a żądanie nie wychodzi.
//  * PRZYCISK TYLKO Z `akcje`. Usługa liczy akcje tymi samymi listami co funkcja bazy,
//    więc panel nie pokazuje przycisku, którego baza i tak by odmówiła.
//  * KAŻDA DECYZJA NIESIE ODCISK karty, którą człowiek widział. Karta zmieniona
//    w Receptorze w międzyczasie (409 `propozycja_zmieniona`) odświeża listę.
//  * ODPOWIEDŹ DLA POPRZEDNIEJ ORGANIZACJI NIE LĄDUJE NA EKRANIE (klucz per żądanie),
//    a nieudane odświeżenie zostawia ostatnią dobrą listę z godziną — jak Dokumenty.
// =============================================================================

import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Modal,
  ModalFooter,
  Spinner,
  Switch,
} from '@insolitum/ui';
import { RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import type { PrawaObiegu } from '../devices/obiegDokumentow';
import {
  KOMUNIKAT_BRAK_RECEPTOR_CORE,
  ReceptorCoreError,
  receptorCoreDostepny,
  rozstrzygnijPropozycje,
  stanKartoteki,
  wlaczKartoteke,
  wylaczKartoteke,
  zwolnijBezpiecznik,
  type AkcjaPropozycji,
  type PropozycjaKartoteki,
  type StanKartoteki,
  type ZrodloKartoteki,
} from '../devices/receptorCoreApi';

import {
  KLASY_PROPOZYCJI_PANELU,
  MAKS_DLUGOSC_POWODU_KARTOTEKI,
  ZDANIE_PRZEPIECIE_INSOLITUM,
  czyZrodloZawieszone,
  etykietaPrzyjecia,
  nazwaTypu,
  stanZakladki,
  walidujWlaczenie,
  zdanieBezpiecznika,
  zdanieKlasy,
  zdaniePowodu,
  zdanieStanuZrodla,
} from './powiazaniaKart';

const godzina = (chwila: number) =>
  new Date(chwila).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });

/** Chwila z bazy (ISO) → „25.10.2026, 08:55” w strefie przeglądarki. */
export function formatujDate(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '—';
  return new Date(ms).toLocaleString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const ZDANIE_KARTA_ZMIENIONA = 'Karta zmieniła się w Receptorze — lista została odświeżona';

export interface ZakladkaPowiazaniaProps {
  organizationId: string | undefined;
  prawa: PrawaObiegu;
  /** Licznik przycisku „Odśwież” w nagłówku strony. */
  odswiezenie: number;
}

/** Wynik przypięty do organizacji, dla której go pobrano. */
interface Przypiety {
  klucz: string;
  wynik: StanKartoteki | 'blad';
}

type Okno =
  | { rodzaj: 'wlacz' }
  | { rodzaj: 'wylacz' }
  | { rodzaj: 'odrzuc'; propozycja: PropozycjaKartoteki };

export function ZakladkaPowiazania({
  organizationId,
  prawa,
  odswiezenie,
}: ZakladkaPowiazaniaProps) {
  const uslugaDostepna = receptorCoreDostepny();
  const mozePytac = prawa.decyduje && Boolean(organizationId) && uslugaDostepna;

  const [dane, setDane] = useState<Przypiety | null>(null);
  const [daneZ, setDaneZ] = useState<number | null>(null);
  const [nieodswiezone, setNieodswiezone] = useState<number | null>(null);
  const [komunikat, setKomunikat] = useState<{ ton: 'error' | 'info'; tresc: string } | null>(null);
  const [pracuje, setPracuje] = useState<string | null>(null);
  const [okno, setOkno] = useState<Okno | null>(null);

  const biezace = useRef({ organizationId, mozePytac });
  biezace.current = { organizationId, mozePytac };

  const pobierz = async () => {
    const { organizationId: org, mozePytac: moze } = biezace.current;
    if (!org || !moze) return;
    const proba = Date.now();
    try {
      const wynik = await stanKartoteki(org);
      if (biezace.current.organizationId !== org) return;
      setDane({ klucz: org, wynik });
      setDaneZ(proba);
      setNieodswiezone(null);
    } catch {
      if (biezace.current.organizationId !== org) return;
      // Nieudane odświeżenie NIE zastępuje dobrej listy błędem.
      setDane((p) =>
        p && p.klucz === org && p.wynik !== 'blad' ? p : { klucz: org, wynik: 'blad' }
      );
      setNieodswiezone(proba);
    }
  };

  useEffect(() => {
    setDane(null);
    setDaneZ(null);
    setNieodswiezone(null);
    setKomunikat(null);
    setOkno(null);
    if (!mozePytac) return;
    void pobierz();
  }, [organizationId, mozePytac]);

  const poprzednieOdswiezenie = useRef(odswiezenie);
  useEffect(() => {
    if (poprzednieOdswiezenie.current === odswiezenie) return;
    poprzednieOdswiezenie.current = odswiezenie;
    void pobierz();
  }, [odswiezenie]);

  const biezaceDane = dane && dane.klucz === organizationId ? dane.wynik : null;
  const stan = stanZakladki({
    prawa,
    organizacja: Boolean(organizationId),
    uslugaDostepna,
    dane: biezaceDane,
  });

  /** Decyzja przez usługę; 409 `propozycja_zmieniona` odświeża listę i mówi dlaczego. */
  const wykonaj = async (klucz: string, dzialanie: () => Promise<unknown>) => {
    const org = biezace.current.organizationId;
    setPracuje(klucz);
    setKomunikat(null);
    try {
      await dzialanie();
      if (biezace.current.organizationId !== org) return true;
      await pobierz();
      return true;
    } catch (e) {
      if (biezace.current.organizationId !== org) return false;
      if (e instanceof ReceptorCoreError && e.kod === 'propozycja_zmieniona') {
        setKomunikat({ ton: 'info', tresc: ZDANIE_KARTA_ZMIENIONA });
        await pobierz();
        return true;
      }
      setKomunikat({
        ton: 'error',
        tresc: e instanceof Error ? e.message : 'Nie udało się wykonać decyzji',
      });
      return false;
    } finally {
      setPracuje(null);
    }
  };

  const decyzja = (p: PropozycjaKartoteki, akcja: AkcjaPropozycji, powod?: string) =>
    wykonaj(`${p.id}:${akcja}`, () =>
      rozstrzygnijPropozycje(organizationId!, p.id, { decyzja: akcja, odcisk: p.odcisk, powod })
    );

  // ── Rysowanie ──────────────────────────────────────────────────────────────

  if (stan === 'brak_prawa' || stan === 'bez_organizacji') {
    return (
      <div data-testid="zakladka-powiazania" data-stan={stan}>
        <EmptyState
          variant="card"
          title={
            stan === 'brak_prawa'
              ? 'Powiązania kart rozstrzyga właściciel albo administrator organizacji.'
              : 'Wybierz organizację'
          }
        />
      </div>
    );
  }
  if (stan === 'brak_uslugi') {
    return (
      <div data-testid="zakladka-powiazania" data-stan={stan}>
        <Alert variant="warning">{KOMUNIKAT_BRAK_RECEPTOR_CORE}</Alert>
      </div>
    );
  }
  if (stan === 'ladowanie') {
    return (
      <div data-testid="zakladka-powiazania" data-stan={stan} className="flex justify-center p-8">
        <Spinner />
      </div>
    );
  }
  if (stan === 'blad') {
    return (
      <div data-testid="zakladka-powiazania" data-stan={stan}>
        <Alert variant="error" title="Nie udało się pobrać powiązań kart">
          <Button variant="secondary" size="sm" className="mt-2" onClick={() => void pobierz()}>
            <RefreshCw className="w-4 h-4" />
            Spróbuj ponownie
          </Button>
        </Alert>
      </div>
    );
  }
  if (stan === 'brak_zrodla' || biezaceDane === null || biezaceDane === 'blad') {
    return (
      <div data-testid="zakladka-powiazania" data-stan={stan}>
        <EmptyState
          variant="card"
          title="Kartoteka z Receptora jeszcze nie dotarła"
          description="Źródło powstaje przy pierwszym spisie kartoteki (agent Receptora 0.5.9 lub nowszy, co ok. 5 minut)."
        />
      </div>
    );
  }

  const d = biezaceDane;
  const zrodlo = d.zrodlo as ZrodloKartoteki;
  const zawieszone = czyZrodloZawieszone(zrodlo.stan);
  const aktywne = zrodlo.stan === 'aktywne';
  const bezAgenta = zrodlo.agent_id === null;

  return (
    <div className="space-y-4" data-testid="zakladka-powiazania" data-stan={stan}>
      {nieodswiezone !== null && daneZ !== null && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-aurora-warning">
          <span data-testid="nieodswiezone">
            Nie udało się odświeżyć ({godzina(nieodswiezone)}) — pokazujemy dane z {godzina(daneZ)}.
          </span>
          <Button variant="ghost" size="sm" onClick={() => void pobierz()}>
            Spróbuj ponownie
          </Button>
        </div>
      )}
      {komunikat && (
        <Alert variant={komunikat.ton} data-testid="komunikat-powiazan">
          {komunikat.tresc}
        </Alert>
      )}
      {zawieszone && (
        <Alert variant="warning" title={zdanieStanuZrodla(zrodlo.stan)}>
          {ZDANIE_PRZEPIECIE_INSOLITUM}.
        </Alert>
      )}

      <KartaZrodla
        zrodlo={zrodlo}
        nagrobki={d.nagrobki}
        pracuje={pracuje === 'bezpiecznik'}
        onZwolnij={() => void wykonaj('bezpiecznik', () => zwolnijBezpiecznik(organizationId!))}
        akcje={
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={zawieszone || bezAgenta || pracuje !== null}
              title={
                zawieszone
                  ? ZDANIE_PRZEPIECIE_INSOLITUM
                  : bezAgenta
                    ? 'Kartoteki nie prowadzi żaden agent — sparuj agenta Receptora'
                    : undefined
              }
              onClick={() => setOkno({ rodzaj: 'wlacz' })}
            >
              {aktywne ? 'Zmień ustawienia…' : 'Włącz…'}
            </Button>
            {aktywne && (
              <Button
                size="sm"
                variant="secondary"
                disabled={bezAgenta || pracuje !== null}
                onClick={() => setOkno({ rodzaj: 'wylacz' })}
              >
                Wyłącz…
              </Button>
            )}
          </div>
        }
      />

      {d.otwarte === 0 ? (
        <EmptyState
          variant="card"
          data-testid="nic-nie-czeka"
          title="Nic nie czeka na decyzję"
          description="Każda karta z Receptora jest powiązana albo przyjęta automatycznie."
        />
      ) : (
        <div className="space-y-4">
          {d.obcieto && (
            <p className="text-xs text-aurora-muted" data-testid="obcieto">
              Pokazano {d.propozycje.length} z {d.otwarte}.
            </p>
          )}
          {KLASY_PROPOZYCJI_PANELU.filter((k) => (d.wg_klasy[k] ?? 0) > 0).map((klasa) => (
            <SekcjaKlasy
              key={klasa}
              klasa={klasa}
              liczba={d.wg_klasy[klasa] ?? 0}
              propozycje={d.propozycje.filter((p) => p.klasa === klasa)}
              pracuje={pracuje}
              onDecyzja={(p, a) =>
                a === 'odrzuc' ? setOkno({ rodzaj: 'odrzuc', propozycja: p }) : void decyzja(p, a)
              }
            />
          ))}
          {/* Klasa spoza listy panelu (nowsza migracja) — nazwana, nie ukryta. */}
          {Object.keys(d.wg_klasy)
            .filter((k) => !(KLASY_PROPOZYCJI_PANELU as readonly string[]).includes(k))
            .map((klasa) => (
              <SekcjaKlasy
                key={klasa}
                klasa={klasa}
                liczba={d.wg_klasy[klasa] ?? 0}
                propozycje={d.propozycje.filter((p) => p.klasa === klasa)}
                pracuje={pracuje}
                onDecyzja={(p, a) =>
                  a === 'odrzuc' ? setOkno({ rodzaj: 'odrzuc', propozycja: p }) : void decyzja(p, a)
                }
              />
            ))}
        </div>
      )}

      {d.odrzucone.length > 0 && (
        <details className="card p-4" data-testid="odrzucone">
          <summary className="cursor-pointer font-medium">Odrzucone ({d.odrzucone.length})</summary>
          <ul className="mt-3 space-y-3">
            {d.odrzucone.map((p) => (
              <WierszPropozycji
                key={p.id}
                propozycja={p}
                pracuje={pracuje}
                onDecyzja={(a) => void decyzja(p, a)}
              />
            ))}
          </ul>
        </details>
      )}

      {okno?.rodzaj === 'wlacz' && zrodlo.agent_id && (
        <OknoWlaczenia
          zrodlo={zrodlo}
          onAnuluj={() => setOkno(null)}
          onZapisz={async (w) => {
            const ok = await wykonaj('wlacz', () =>
              wlaczKartoteke(organizationId!, zrodlo.agent_id!, w)
            );
            if (ok) setOkno(null);
          }}
          pracuje={pracuje === 'wlacz'}
        />
      )}
      {okno?.rodzaj === 'wylacz' && zrodlo.agent_id && (
        <OknoPowodu
          tytul="Wyłącz przyjmowanie kart z Receptora"
          opis="Karty już powiązane zostają powiązane; ich zmiany z Receptora poczekają do ponownego włączenia."
          etykieta="Powód"
          przycisk="Wyłącz"
          wymagany
          pracuje={pracuje === 'wylacz'}
          onAnuluj={() => setOkno(null)}
          onZapisz={async (powod) => {
            const ok = await wykonaj('wylacz', () =>
              wylaczKartoteke(organizationId!, zrodlo.agent_id!, powod)
            );
            if (ok) setOkno(null);
          }}
        />
      )}
      {okno?.rodzaj === 'odrzuc' && (
        <OknoPowodu
          tytul={`Odrzuć kartę „${okno.propozycja.karta.nazwa ?? 'bez nazwy'}”`}
          opis="Karta nie wróci, dopóki nie zmieni się w Receptorze."
          etykieta="Powód (opcjonalnie)"
          przycisk="Odrzuć"
          wymagany={false}
          pracuje={pracuje === `${okno.propozycja.id}:odrzuc`}
          onAnuluj={() => setOkno(null)}
          onZapisz={async (powod) => {
            const ok = await decyzja(okno.propozycja, 'odrzuc', powod);
            if (ok) setOkno(null);
          }}
        />
      )}
    </div>
  );
}

function KartaZrodla({
  zrodlo,
  nagrobki,
  akcje,
  pracuje,
  onZwolnij,
}: {
  zrodlo: ZrodloKartoteki;
  nagrobki: { nieobecne: number; usuniete: number };
  akcje: ReactNode;
  pracuje: boolean;
  onZwolnij: () => void;
}) {
  const bezpiecznik = zdanieBezpiecznika(zrodlo.obserwacja_wynik);
  const wiersze: Array<[string, string]> = [
    ['Stan', zdanieStanuZrodla(zrodlo.stan)],
    ['Ostatni spis', formatujDate(zrodlo.ostatni_spis_at)],
    ['Ostatni pełny spis', formatujDate(zrodlo.ostatni_pelny_spis_at)],
    ['Kart w Receptorze', zrodlo.kart === null ? '—' : String(zrodlo.kart)],
    ['Automat', zrodlo.automat ? 'Tak' : 'Nie'],
    [
      'VAT potwierdzony',
      zrodlo.vat_potwierdzono_at ? formatujDate(zrodlo.vat_potwierdzono_at) : 'Nie',
    ],
    [
      'Przenosiny zatwierdzone',
      zrodlo.przenosiny_zatwierdzono_at ? formatujDate(zrodlo.przenosiny_zatwierdzono_at) : 'Nie',
    ],
    [
      'Ostatnia porażka',
      zrodlo.ostatnia_porazka_kod
        ? `${zrodlo.ostatnia_porazka_kod} (z rzędu: ${zrodlo.porazki_z_rzedu})`
        : 'Brak',
    ],
  ];
  return (
    <section className="card p-4 space-y-3" data-testid="stan-zrodla">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold">Stan źródła</h2>
        {akcje}
      </div>
      <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
        {wiersze.map(([nazwa, wartosc]) => (
          <div key={nazwa} className="flex gap-2">
            <dt className="text-aurora-muted">{nazwa}:</dt>
            <dd>{wartosc}</dd>
          </div>
        ))}
      </dl>
      <p className="text-sm" data-testid="nagrobki">
        Usunięte w Receptorze: {nagrobki.usuniete}, nieobecne w ostatnim spisie:{' '}
        {nagrobki.nieobecne}
      </p>
      {bezpiecznik &&
        (zrodlo.bezpiecznik_zwolniony ? (
          <p className="text-sm text-aurora-muted" data-testid="bezpiecznik-zwolniony">
            Zatwierdzone — oznaczy przy następnym spisie
          </p>
        ) : (
          <Alert variant="warning" data-testid="bezpiecznik">
            <p>{bezpiecznik}</p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-2"
              disabled={pracuje}
              onClick={onZwolnij}
            >
              Potwierdzam — oznacz przy następnym spisie
            </Button>
          </Alert>
        ))}
    </section>
  );
}

function SekcjaKlasy({
  klasa,
  liczba,
  propozycje,
  pracuje,
  onDecyzja,
}: {
  klasa: string;
  liczba: number;
  propozycje: PropozycjaKartoteki[];
  pracuje: string | null;
  onDecyzja: (p: PropozycjaKartoteki, a: AkcjaPropozycji) => void;
}) {
  const { tytul, zdanie } = zdanieKlasy(klasa);
  return (
    <section className="card p-4 space-y-3" data-testid={`klasa-${klasa}`}>
      <div>
        <h3 className="font-semibold">
          {tytul} · {liczba}
        </h3>
        <p className="text-sm text-aurora-muted">{zdanie}</p>
      </div>
      <ul className="space-y-3">
        {propozycje.map((p) => (
          <WierszPropozycji
            key={p.id}
            propozycja={p}
            pracuje={pracuje}
            onDecyzja={(a) => onDecyzja(p, a)}
          />
        ))}
      </ul>
    </section>
  );
}

function WierszPropozycji({
  propozycja: p,
  pracuje,
  onDecyzja,
}: {
  propozycja: PropozycjaKartoteki;
  pracuje: string | null;
  onDecyzja: (a: AkcjaPropozycji) => void;
}) {
  const k = p.karta;
  const przyjecie = p.akcje.includes('przyjmij') ? etykietaPrzyjecia(p.klasa) : null;
  const zajety = pracuje !== null;
  return (
    <li
      className="border-t border-aurora-border pt-3 first:border-t-0 first:pt-0"
      data-testid={`propozycja-${p.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="space-y-1 text-sm">
          <p className="font-medium">{k.nazwa ?? 'Karta bez nazwy'}</p>
          <p className="text-aurora-muted">
            Symbol: {k.symbol ?? '—'} · {nazwaTypu(k.typ)} · {k.jednostka ?? '—'} · VAT{' '}
            {k.vat === null ? '—' : `${k.vat}%`} · EAN {k.ean ?? '—'}
          </p>
          {p.encja && (
            <p className="text-aurora-muted" data-testid="karta-universe">
              Karta Universe: {p.encja.nazwa ?? '—'} ({p.encja.kod ?? 'bez kodu'})
            </p>
          )}
          {p.powody.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {p.powody.map((w) => {
                const z = zdaniePowodu(w);
                return (
                  <Badge key={w} variant="warning" title={z.podpowiedz}>
                    {z.etykieta}
                  </Badge>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {przyjecie && (
            <Button size="sm" disabled={zajety} onClick={() => onDecyzja('przyjmij')}>
              {przyjecie}
            </Button>
          )}
          {!p.akcje.includes('przyjmij') && p.stan === 'otwarta' && (
            <span className="text-xs text-aurora-muted self-center">Popraw kartę w Receptorze</span>
          )}
          {p.akcje.includes('odrzuc') && (
            <Button
              size="sm"
              variant="secondary"
              disabled={zajety}
              onClick={() => onDecyzja('odrzuc')}
            >
              Odrzuć…
            </Button>
          )}
          {p.akcje.includes('ponow') && (
            <Button
              size="sm"
              variant="ghost"
              disabled={zajety}
              title="Universe oceni kartę przy następnym spisie, do 5 minut"
              onClick={() => onDecyzja('ponow')}
            >
              Sprawdź ponownie
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}

function OknoWlaczenia({
  zrodlo,
  pracuje,
  onAnuluj,
  onZapisz,
}: {
  zrodlo: ZrodloKartoteki;
  pracuje: boolean;
  onAnuluj: () => void;
  onZapisz: (w: {
    automat: boolean;
    vat_z_receptora: boolean;
    przenosiny_zgodne: boolean;
  }) => Promise<void>;
}) {
  const [automat, setAutomat] = useState(zrodlo.automat);
  // Potwierdzenie VAT ZA KAŻDYM RAZEM — nie dziedziczy wcześniejszego.
  const [vat, setVat] = useState(false);
  const [przenosiny, setPrzenosiny] = useState(false);
  const walidacja = walidujWlaczenie({ automat, vatZReceptora: vat });
  const aktywne = zrodlo.stan === 'aktywne';
  return (
    <Modal
      open
      onClose={onAnuluj}
      title={aktywne ? 'Ustawienia przyjmowania kart' : 'Włącz przyjmowanie kart z Receptora'}
      footer={
        <ModalFooter>
          <Button variant="ghost" onClick={onAnuluj} disabled={pracuje}>
            Anuluj
          </Button>
          <Button
            onClick={() =>
              void onZapisz({
                automat,
                vat_z_receptora: vat,
                przenosiny_zgodne: zrodlo.przenosiny_zatwierdzono_at ? false : przenosiny,
              })
            }
            disabled={!walidacja.ok || pracuje}
          >
            {pracuje && <Spinner size="sm" />}
            {aktywne ? 'Zapisz' : 'Włącz'}
          </Button>
        </ModalFooter>
      }
    >
      <div className="space-y-4 text-sm">
        <Switch
          label="Przyjmuj nowe karty automatycznie"
          checked={automat}
          onChange={(e) => setAutomat(e.target.checked)}
        />
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={vat}
            onChange={(e) => setVat(e.target.checked)}
            className="mt-1"
          />
          <span>
            Potwierdzam, że stawki VAT kart w Receptorze są prawdziwe — Universe przyjmie je bez
            sprawdzania każdej karty.
          </span>
        </label>
        {zrodlo.przenosiny_zatwierdzono_at ? (
          <p className="text-aurora-muted" data-testid="przenosiny-zatwierdzone">
            Przenosiny kart z dawnego importu: zatwierdzone{' '}
            {formatujDate(zrodlo.przenosiny_zatwierdzono_at)}
          </p>
        ) : (
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={przenosiny}
              onChange={(e) => setPrzenosiny(e.target.checked)}
              className="mt-1"
            />
            <span>
              Przenieś hurtem karty zgodne z dawnym importem (ten sam numer karty w Receptorze i ta
              sama nazwa)
            </span>
          </label>
        )}
        {!walidacja.ok && (
          <p className="text-aurora-danger" data-testid="powod-wlaczenia">
            {walidacja.powod}
          </p>
        )}
        <p className="text-aurora-muted">
          Od włączenia karty powiązane z Receptorem zmienia wyłącznie Receptor — w panelu nie
          założysz nowego produktu, a nazwy, jednostki, VAT i EAN tych kart poprawia się w
          Receptorze.
        </p>
      </div>
    </Modal>
  );
}

function OknoPowodu({
  tytul,
  opis,
  etykieta,
  przycisk,
  wymagany,
  pracuje,
  onAnuluj,
  onZapisz,
}: {
  tytul: string;
  opis: string;
  etykieta: string;
  przycisk: string;
  wymagany: boolean;
  pracuje: boolean;
  onAnuluj: () => void;
  onZapisz: (powod: string) => Promise<void>;
}) {
  const [powod, setPowod] = useState('');
  const pusty = powod.trim() === '';
  return (
    <Modal
      open
      onClose={onAnuluj}
      title={tytul}
      description={opis}
      footer={
        <ModalFooter>
          <Button variant="ghost" onClick={onAnuluj} disabled={pracuje}>
            Anuluj
          </Button>
          <Button
            onClick={() => void onZapisz(powod.trim())}
            disabled={(wymagany && pusty) || pracuje}
          >
            {pracuje && <Spinner size="sm" />}
            {przycisk}
          </Button>
        </ModalFooter>
      }
    >
      <label className="block text-sm">
        {etykieta}
        <textarea
          className="input mt-1 block w-full"
          rows={3}
          maxLength={MAKS_DLUGOSC_POWODU_KARTOTEKI}
          value={powod}
          onChange={(e) => setPowod(e.target.value)}
        />
      </label>
    </Modal>
  );
}
